import { useEffect, useState, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Droplet, Upload, Loader2 } from "lucide-react";
import { money } from "@/lib/format";
import { HelpHint } from "@/components/HelpHint";
import { toast } from "sonner";

// Optional ink/machine picker embedded in the estimate. Upload the artwork to auto-detect
// ink coverage; the detected % feeds straight into the quote (ink cost is added to the price).
export function InkPicker({ machineId, setMachineId, coverage, setCoverage, categories, estimateSize, defaultMatch }) {
  const [machines, setMachines] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detected, setDetected] = useState(null);
  const applied = useRef(false);

  useEffect(() => {
    api.get("/machines").then((r) => {
      let list = r.data || [];
      if (categories) list = list.filter((m) => categories.includes(m.category));
      setMachines(list);
      if (!applied.current && defaultMatch && (machineId === "none" || !machineId)) {
        const m = list.find((x) => (x.name || "").toLowerCase().includes(defaultMatch.toLowerCase()));
        if (m) { applied.current = true; setMachineId(m.id); }
      }
    }).catch(() => {});
  }, [categories]);

  const analyze = async (f) => {
    setFile(f); setDetected(null);
    if (!f) return;
    if (!machineId || machineId === "none") { toast.error("Select a machine first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("machine_id", machineId);
      fd.append("width_in", estimateSize?.width || 24);
      fd.append("height_in", estimateSize?.height || 18);
      fd.append("quantity", 1);
      fd.append("file", f);
      const { data } = await api.post("/ink/estimate", fd);
      setDetected(data);
      if (data.coverage_pct != null) setCoverage(Math.round(data.coverage_pct));
      toast.success(`Detected ${Math.round(data.coverage_pct)}% ink coverage from file`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-slate-200 p-4 mt-5" data-testid="ink-picker">
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">
        <Droplet size={13} /> Machine & Ink (optional)
      </div>
      <Label className="text-xs flex items-center gap-1">Machine<HelpHint side="right">Optional. Pick the printer to add <b>ink cost</b> to the quote. Leave on &ldquo;No ink cost&rdquo; to price media only. Each machine has its own ink price per ml.</HelpHint></Label>
      <Select value={machineId} onValueChange={setMachineId}>
        <SelectTrigger data-testid="ink-picker-machine" className="rounded-lg mt-1"><SelectValue placeholder="No ink cost" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— No ink cost —</SelectItem>
          {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
        </SelectContent>
      </Select>

      {machineId && machineId !== "none" && (
        <>
          <div className="mt-3">
            <Label className="text-xs flex items-center gap-1">Analyze artwork file (auto ink coverage)<HelpHint side="right">Upload the art (PDF/PNG/JPG/TIFF) and the system measures how much of the sheet is inked, then sets the ink coverage % automatically &mdash; more accurate than guessing.</HelpHint></Label>
            <label className="mt-1 flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-3 py-2.5 cursor-pointer hover:border-[#2495D3]" data-testid="ink-picker-file-label">
              {busy ? <Loader2 size={15} className="animate-spin text-[#2495D3]" /> : <Upload size={15} className="text-slate-400" />}
              <span className="text-sm text-slate-500 truncate">{busy ? "Analyzing…" : (file ? file.name : "Upload PDF / PNG / JPG / TIFF")}</span>
              <input data-testid="ink-picker-file" type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => analyze(e.target.files?.[0] || null)} />
            </label>
            {detected && (
              <p className="text-[11px] text-emerald-600 mt-1.5" data-testid="ink-picker-detected">
                Detected {Math.round(detected.coverage_pct)}% coverage · {detected.ink_ml} ml · {money(detected.ink_cost)} ink for this size
              </p>
            )}
          </div>

          <div className="mt-3">
            <div className="flex justify-between">
              <Label className="text-xs flex items-center gap-1">Ink coverage {file ? "(from file — adjust if needed)" : ""}<HelpHint side="right">How much of the sheet is covered in ink. Higher coverage = more ink cost. Pick a rough level (25&ndash;100%) or upload the file above to detect it exactly.</HelpHint></Label>
              <span className="num text-xs font-bold text-[#2495D3]">{coverage}%</span>
            </div>
            <div className="flex gap-2 mt-1">
              {[25, 50, 75, 100].map((c) => (
                <button key={c} type="button" data-testid={`ink-picker-cov-${c}`} onClick={() => { setCoverage(c); setFile(null); setDetected(null); }}
                  className={`flex-1 py-1 text-xs rounded-lg border ${coverage === c && !file ? "bg-[#2495D3] text-white border-[#2495D3]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{c}%</button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
